import {
  ProjectAppTypes,
  RestrictTo,
  useConfig,
  useDate,
  useEnvProvider,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { Box, Checkbox, Text } from '@bechtel/gds';
import { PersonaType, Weld } from '@bechtel/types';
import { parseISO } from 'date-fns';
import { filter, find, findIndex, map, some } from 'lodash';
import { FC } from 'react';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';
import { FitupStatusStyled, WeldItemStyled, WeldStatusStyled } from './styled';
import WeldAssignee from './weld-assignee';
interface WeldItemProps {
  selectedWeld: Weld | undefined;
  setSelectedWeld: (weld: Weld) => void;
  selectedWeldIds: string[];
  setSelectedWeldIds: (ids: string[]) => void;
  weld: Weld;
  isFitupRequired: boolean;
}

const WeldItem: FC<WeldItemProps> = ({
  selectedWeld,
  setSelectedWeld,
  selectedWeldIds,
  setSelectedWeldIds,
  weld,
  isFitupRequired,
}) => {
  const { envConfig } = useEnvProvider();
  const hasFitupMilestone = some(weld?.milestones, (item) => {
    return item.name.toUpperCase() === WeldMilestoneType.FITUP;
  });
  const weldCurrentMilestone = weld?.milestones.find((milestone) => {
    return milestone?.type?.id === weld?.currentMilestoneType?.id;
  });
  const installCommodityAssignee = find(weld?.commodityAssignees, (item) => {
    return item.personaType == PersonaType.FOREMAN;
  });

  const { formatRelativeInWords } = useDate();
  const { dateFormat } = useConfig();
  const { assignedCrewMembers } = useWorkPackage();
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  return (
    <WeldItemStyled isActive={selectedWeld?.id == weld.id}>
      <Box mt={{ sm: '400' }} mr={{ sm: '400' }}>
        {canCreateFitupRequest && envConfig.appName === ProjectAppTypes.PF && (
          <Checkbox
            checked={some(selectedWeldIds, (item) => item === weld?.id)}
            onChange={() => {
              const selected = findIndex(selectedWeldIds, (item) => {
                return item === weld?.id;
              });
              if (selected !== -1) {
                setSelectedWeldIds(filter(selectedWeldIds, (weldId) => weldId !== weld?.id));
              } else {
                setSelectedWeldIds([...selectedWeldIds, weld?.id]);
              }
            }}
            readOnly
          />
        )}
      </Box>
      <Box
        flexDirection={{ sm: 'column' }}
        onClick={() => setSelectedWeld(weld)}
        flexGrow={{ sm: 1 }}
        data-testid={`weld-list-item-${weld?.id}`}
      >
        <Box
          pt={{ sm: '400' }}
          pb={{ sm: '100' }}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
        >
          <Text size="400" weight="600" color="grey.1000">
            {weld.tag}
          </Text>
          <Text size="300" weight="600" color="grey.600">
            {weld.type.name}
          </Text>
        </Box>

        <Box pt={{ sm: '450' }} flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '500' }}>
            {!hasFitupMilestone && (
              <FitupStatusStyled
                selected={
                  isFitupRequired && (weld.id === selectedWeld?.id || some(selectedWeldIds, (item) => item === weld.id))
                }
              />
            )}
            {weld?.milestones &&
              weld?.milestones.length > 0 &&
              map(weld?.milestones, (milestone) => {
                return (
                  <WeldStatusStyled
                    key={milestone.id}
                    status={milestone.currentStatus.codeI18n}
                    isAssigned={!!(installCommodityAssignee?.assignee ?? weld?.fitupRequest?.installationCrewMember)}
                    hasBlocks={milestone.blocks.length > 0}
                    isFitupRequired={!!weld.isFitupRequired}
                    isFitupMilestone={milestone?.name?.toUpperCase() === WeldMilestoneType.FITUP}
                  />
                );
              })}
          </Box>

          <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
            <WeldAssignee assignee={installCommodityAssignee?.assignee ?? weld?.fitupRequest?.installationCrewMember} />
            <Box ml={{ sm: '400' }}>
              <Text size="300" color="grey.600" style={{ fontStyle: 'italic' }}>
                {formatRelativeInWords(
                  parseISO(weldCurrentMilestone?.currentStatus?.createdAt),
                  new Date(),
                  RestrictTo.Day,
                  dateFormat,
                  ``,
                  false
                )}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </WeldItemStyled>
  );
};

export default WeldItem;
